import React, { useEffect, useState } from 'react';
import { fetchRecurrentes, toggleRecurrenteActive } from '../../services/General/recurrenteService';
import { Recurrente } from '../../types/recurrente';
import RecurrenteModal from './RecurrenteModal';
import { useAuth } from '../../context/AuthContext';
import Swal from 'sweetalert2';

const RecurrenteList: React.FC = () => {
    const { user: userContext } = useAuth();
    const [recurrentes, setRecurrentes] = useState<Recurrente[]>([]);
    const [filteredRecurrentes, setFilteredRecurrentes] = useState<Recurrente[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedRecurrente, setSelectedRecurrente] = useState<Recurrente>({
        id: 0,
        guest_cedula: '',
        guest_name: '',
        guest_email: '',
        host_email: '',
        building_id: 0,
        motivo: '',
        frecuencia: null,
        dia_preferido: null,
        fecha_inicio: '',
        fecha_fin: null,
        expiry_date: '',
        max_visitas_sin_cita: 10,
        visitas_realizadas: 0,
        ultima_visita: null,
        created_by: '',
        date_created: '',
        updated_by: null,
        date_updated: null,
        is_active: true
    });
    const [showModal, setShowModal] = useState<boolean>(false);

    const loadRecurrentes = async () => {
        setLoading(true);
        try {
            const data = await fetchRecurrentes('', userContext?.User.email);
            setRecurrentes(data);
            setFilteredRecurrentes(data);
            console.log('Recurrentes cargadas:', data);
        } catch (error: any) {
            console.error('Error fetching recurrentes:', error);
            setError(error.message || 'Error al cargar recurrentes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRecurrentes();
    }, []);

    const handleAddEdit = (recurrente?: Recurrente) => {
        console.log('Recurrente por editar:', recurrente);
        setSelectedRecurrente(recurrente || {
            id: 0,
            guest_cedula: '',
            guest_name: '',
            guest_email: '',
            host_email: userContext?.User.email || '',
            building_id: 0,
            motivo: '',
            frecuencia: null,
            dia_preferido: null,
            fecha_inicio: '',
            fecha_fin: null,
            expiry_date: '',
            max_visitas_sin_cita: 10,
            visitas_realizadas: 0,
            ultima_visita: null,
            created_by: '',
            date_created: '',
            updated_by: null,
            date_updated: null,
            is_active: true
        });
        setShowModal(true);
    };

    const handleToggleActive = async (recurrente: Recurrente) => {
        const action = recurrente.is_active ? 'desactivar' : 'activar';
        const result = await Swal.fire({
            title: `¿${action} visita recurrente?`,
            text: `Esta acción ${recurrente.is_active ? 'deshabilitará' : 'habilitará'} el acceso recurrente para ${recurrente.guest_name}`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: recurrente.is_active ? '#d33' : '#3085d6',
            confirmButtonText: `Sí, ${action}`,
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await toggleRecurrenteActive(recurrente.id, userContext?.User.email || '');
                await loadRecurrentes();
                Swal.fire({
                    title: '¡Éxito!',
                    text: `Visita recurrente ${action}da correctamente`,
                    icon: 'success',
                    timer: 2000
                });
            } catch (error: any) {
                Swal.fire({
                    title: 'Error',
                    text: error.message || `No se pudo ${action} la visita recurrente`,
                    icon: 'error'
                });
            }
        }
    };

    const handleSave = async (recurrente: Recurrente, isNew: boolean) => {
        setLoading(true);
        try {
            await loadRecurrentes(); // Recargar lista después de guardar
            setShowModal(false);
        } catch (err: any) {
            setError(err.message || 'Error al guardar');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) return <div className='container mt-4'>Cargando visitas recurrentes...</div>;
    if (error) return <div className='container mt-4 text-danger'>Error: {error}</div>;

    return (
        <>
            <div className='container mt-4'>
                <div className='card shadow'>
                    <div className='card-header bg-primary text-white d-flex justify-content-between align-items-center'>
                        <h5 className='mb-0'>
                            <i className='fas fa-repeat me-2'></i>
                            Gestión de Visitas Recurrentes
                        </h5>
                        <button
                            className='btn btn-light btn-sm'
                            onClick={() => handleAddEdit()}
                        >
                            <i className='fas fa-plus me-1'></i>
                            Nueva Recurrente
                        </button>
                    </div>

                    <div className='card-body'>
                        {/* Buscador */}
                        <div className='mb-3'>
                            <input
                                type='text'
                                className='form-control'
                                placeholder='Buscar por nombre, cédula o anfitrión...'
                                onChange={(e) => {
                                    const searchTerm = e.target.value.toLowerCase();
                                    if (searchTerm === '') {
                                        setFilteredRecurrentes(recurrentes);
                                        return;
                                    }
                                    const filtered = recurrentes.filter(r =>
                                        r.guest_name.toLowerCase().includes(searchTerm) ||
                                        r.guest_cedula.toLowerCase().includes(searchTerm) ||
                                        r.host_email.toLowerCase().includes(searchTerm) ||
                                        (r.motivo && r.motivo.toLowerCase().includes(searchTerm))
                                    );
                                    setFilteredRecurrentes(filtered);
                                }}
                            />
                        </div>

                        {/* Tabla */}
                        <div className='table-responsive' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <table className='table table-hover table-striped'>
                                <thead className='sticky-top bg-white' style={{ zIndex: 10 }}>
                                    <tr>
                                        <th>#</th>
                                        <th>Visitante</th>
                                        <th>Cédula</th>
                                        <th>Anfitrión</th>
                                        <th>Motivo</th>
                                        <th>Visitas</th>
                                        <th>Vigencia</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRecurrentes.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className='text-center py-4 text-muted'>
                                                No hay visitas recurrentes registradas
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRecurrentes.map((r) => (
                                            <tr key={r.id}>
                                                <td>{r.id}</td>
                                                <td>
                                                    <strong>{r.guest_name}</strong>
                                                    {r.guest_email && (
                                                        <div><small className='text-muted'>{r.guest_email}</small></div>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className='badge bg-secondary'>{r.guest_cedula}</span>
                                                </td>
                                                <td>
                                                    <small>{r.host_email}</small>
                                                    {r.building_name && (
                                                        <div>
                                                            <span><small className='text-muted'>Edificio: {r.building_name}</small></span>
                                                        </div>
                                                    )}
                                                    {r.frecuencia && (
                                                        <div>
                                                            <span className='badge bg-info'><small>{r.frecuencia}</small></span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <small>{r.motivo}</small>
                                                    {r.dia_preferido && (
                                                        <div><small className='text-muted'>Día: {r.dia_preferido}</small></div>
                                                    )}
                                                </td>
                                                <td className='text-center'>
                                                    <span className={`badge ${r.visitas_realizadas >= r.max_visitas_sin_cita ? 'bg-warning' : 'bg-success'}`}>
                                                        {r.visitas_realizadas}/{r.max_visitas_sin_cita}
                                                    </span>
                                                    {r.ultima_visita && (
                                                        <div><small className='text-muted'>{formatDate(r.ultima_visita)}</small></div>
                                                    )}
                                                </td>
                                                <td>
                                                    {r.fecha_fin ? (
                                                        <span className={`badge ${new Date(r.fecha_fin) < new Date() ? 'bg-danger' : 'bg-success'}`}>
                                                            {formatDate(r.fecha_fin)}
                                                        </span>
                                                    ) : (
                                                        <span className='badge bg-success'>Indefinido</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`badge ${r.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                        {r.is_active ? 'Activa' : 'Inactiva'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className='btn btn-sm btn-outline-primary me-2'
                                                        onClick={() => handleAddEdit(r)}
                                                        title='Editar'
                                                    >
                                                        <i className='fas fa-edit'></i>
                                                    </button>
                                                    <button
                                                        className={`btn btn-sm ${r.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                        onClick={() => handleToggleActive(r)}
                                                        title={r.is_active ? 'Desactivar' : 'Activar'}
                                                    >
                                                        <i className={`fas ${r.is_active ? 'fa-ban' : 'fa-check'}`}></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {showModal && selectedRecurrente && (
                    <RecurrenteModal
                        recurrente={selectedRecurrente}
                        onClose={() => setShowModal(false)}
                        onSave={handleSave}
                    />
                )}
            </div>
        </>

    );
};

export default RecurrenteList;